import { BASE } from '../config/api';
import { handleAttendanceUpsert, handleAttendanceCreated, handleAttendanceLocked, handlePunchCreated } from './handlers/attendance';
import { agendaSelectiveRefresh } from '../api/bootstrap';
import sseTokenService from '../services/sseTokenService';

let evs = null;
let currentUserId = null;

async function sseUrl(userId) {
  // Utiliser le service de tokens SSE pour obtenir un token temporaire
  const sseToken = await sseTokenService.getCurrentToken();
  console.log('🔗 [SSE] URL générée avec token SSE temporaire');
  // Utiliser l'endpoint SSE correct du backend Stericab
  const sseEndpoint = `${BASE}/api/sse/dashboard?jwt=${encodeURIComponent(sseToken)}`;
  console.log('🔗 [SSE] URL construite:', sseEndpoint);
  return sseEndpoint;
}

export async function startSSE(userId) {
  if (!userId) return;
  if (evs && currentUserId === String(userId)) return; // déjà connecté
  stopSSE(); // reset propre

  currentUserId = String(userId);
  
  try {
    const url = await sseUrl(userId);
    console.log('🔍 [SSE] URL générée:', url);
    console.log('🔍 [SSE] UserId:', userId);
    console.log('🔍 [SSE] Token SSE valide:', sseTokenService.isValid());

    evs = new EventSource(url, { withCredentials: true });
  } catch (e) {
    console.warn('❌ [SSE] Échec initialisation:', e);
    return;
  }

  evs.onopen = () => {
    console.log('✅ [SSE] Connexion établie avec token SSE temporaire');
    console.log('🔍 [SSE] Token valide jusqu\'à:', new Date(sseTokenService.expiresAt).toISOString());
  };
  
  evs.onerror = (e) => {
    console.warn('❌ [SSE] Erreur de connexion:', e);
    
    // Si erreur 401/403, le token SSE est probablement expiré
    if (e.target?.readyState === EventSource.CLOSED) {
      console.log('🔄 [SSE] Connexion fermée, vérification du token...');
      
      // Vérifier si le token est expiré
      if (!sseTokenService.isValid()) {
        console.log('🔄 [SSE] Token expiré, renouvellement automatique...');
        sseTokenService.forceRenew().then(() => {
          if (currentUserId) {
            console.log('🔄 [SSE] Reconnexion avec nouveau token...');
            startSSE(currentUserId);
          }
        });
      } else {
        // Token valide mais connexion fermée, réessayer
        setTimeout(() => {
          if (currentUserId) {
            console.log('🔄 [SSE] Reconnexion automatique...');
            startSSE(currentUserId);
          }
        }, 60000); // 1 minute au lieu de 30s
      }
    }
  };

  const on = (name, fn) => {
    evs.addEventListener(name, (evt) => {
      console.log(`🔥 [SSE] Événement reçu: ${name}`, evt.data);
      localStorage.setItem('lastSSEActivity', String(Date.now()));
      try {
        const data = evt?.data ? JSON.parse(evt.data) : null;
        fn(data);
      } catch (e) {
        console.warn(`[SSE] parse error on ${name}:`, e);
      }
    });
  };

  // 🔍 DIAGNOSTIC: Écouter TOUS les événements SSE pour voir ce qui arrive vraiment
  evs.addEventListener('message', (evt) => {
    console.log('🔍 [SSE DIAGNOSTIC] Événement générique reçu:', {
      type: evt.type,
      data: evt.data,
      lastEventId: evt.lastEventId,
      origin: evt.origin
    });
    
    // Essayer de parser les données pour voir le format
    try {
      const parsed = JSON.parse(evt.data);
      console.log('🔍 [SSE DIAGNOSTIC] Données parsées:', parsed);
      
      // Détecter si c'est un événement d'agenda
      if (parsed && (
        parsed.type?.includes('override') || 
        parsed.event?.includes('override') ||
        parsed.operator_id ||
        parsed.date
      )) {
        console.log('🎯 [SSE DIAGNOSTIC] Événement agenda détecté!', parsed);
        
        // Essayer de traiter comme événement d'agenda
        if (parsed.type === 'schedule.override.updated' || parsed.event === 'schedule.override.updated') {
          console.log('🔄 [SSE DIAGNOSTIC] Traitement comme schedule.override.updated');
          agendaSelectiveRefresh({ type: 'op:override:upsert', ...parsed });
        } else if (parsed.type === 'schedule.override.deleted' || parsed.event === 'schedule.override.deleted') {
          console.log('🔄 [SSE DIAGNOSTIC] Traitement comme schedule.override.deleted');
          agendaSelectiveRefresh({ type: 'op:override:delete', ...parsed });
        }
      }
    } catch (e) {
      console.log('🔍 [SSE DIAGNOSTIC] Impossible de parser:', evt.data);
    }
  });

  // Heartbeat facultatif - tracker d'activité SSE
  on('hb', () => {
    localStorage.setItem('lastSSEActivity', String(Date.now()));
  });

  // Attendance temps réel (aligné avec backend)
  on('attendance.day.updated',  handleAttendanceUpsert);
  on('attendance.day.created',  handleAttendanceCreated);
  on('attendance.day.locked',   handleAttendanceLocked);
  on('attendance.punch.created',handlePunchCreated);

  // 🔥 Overrides temps réel pour l'Agenda - ÉVÉNEMENTS BACKEND RÉELS
  on('schedule.override.updated', (data) => {
    console.log('🔥 [SSE] Événement schedule.override.updated reçu:', data);
    localStorage.setItem('lastSSEActivity', String(Date.now()));
    
    // Adapter le format pour agendaSelectiveRefresh
    const adaptedData = {
      type: 'op:override:upsert',
      operator_id: data.payload?.operator_id || data.operator_id,
      date: data.payload?.date || data.date,
      is_off: data.payload?.override?.is_off,
      start_local: data.payload?.override?.start_local,
      end_local: data.payload?.override?.end_local,
      end_next_day: data.payload?.override?.end_next_day,
      tz: data.payload?.override?.tz,
      note: data.payload?.override?.note,
      ...data.payload
    };
    console.log('🔄 [SSE] Données adaptées pour agendaSelectiveRefresh:', adaptedData);
    agendaSelectiveRefresh(adaptedData);
  });
  
  on('schedule.override.deleted', (data) => {
    console.log('🔥 [SSE] Événement schedule.override.deleted reçu:', data);
    localStorage.setItem('lastSSEActivity', String(Date.now()));
    
    // Adapter le format pour agendaSelectiveRefresh
    const adaptedData = {
      type: 'op:override:delete',
      operator_id: data.payload?.operator_id || data.operator_id,
      date: data.payload?.date || data.date,
      ...data.payload
    };
    console.log('🔄 [SSE] Données adaptées pour agendaSelectiveRefresh:', adaptedData);
    agendaSelectiveRefresh(adaptedData);
  });
  
  // Garder les anciens événements pour compatibilité
  on('op:override:upsert', (data) => {
    console.log('🔥 [SSE] Événement op:override:upsert reçu (legacy):', data);
    agendaSelectiveRefresh({ type: 'op:override:upsert', ...data });
  });
  on('op:override:delete', (data) => {
    console.log('🔥 [SSE] Événement op:override:delete reçu (legacy):', data);
    agendaSelectiveRefresh({ type: 'op:override:delete', ...data });
  });
  on('op:weekly_hours:update', (data) => {
    console.log('🔥 [SSE] Événement op:weekly_hours:update reçu:', data);
    agendaSelectiveRefresh({ type: 'op:weekly_hours:update', ...data });
  });
  on('op:update', (data) => {
    console.log('🔥 [SSE] Événement op:update reçu:', data);
    agendaSelectiveRefresh({ type: 'op:update', ...data });
  });
}

export function stopSSE() {
  if (evs) {
    try { 
      evs.close(); 
      console.log('🔌 [SSE] Connexion fermée');
    } catch {}
  }
  evs = null;
  currentUserId = null;
}

// Écouter les événements de renouvellement de token
window.addEventListener('sse-token-renewed', (event) => {
  console.log('🔄 [SSE] Token SSE renouvelé, redémarrage de la connexion...');
  if (currentUserId) {
    // Redémarrer la connexion SSE avec le nouveau token
    setTimeout(() => {
      startSSE(currentUserId);
    }, 1000);
  }
});
